import React from 'react';
import {
    S38NearbyLocations,
    type MilanBackgroundColors,
    useBackgroundOptions,
    Typography,
} from '@milanlaser/mlcomponents';
import { MedicalDirector, Metro, State, Store } from 'clinics/clinics';
import { type PaddingSize, paddingMap } from '../../utils/builderPaddingOptions';
import { processBackgroundColor } from '../../utils/builder.utils';
import { ReviewsData } from '../../../../utils/ssr/types';
import { getClosestStores } from '../../../../utils/ssr/utils/geo';

function hardTrim(str: string) {
    return str.trim().replace(/[^a-z0-9]/gi, '');
}

type BuilderState<T> = {
    device: string;
    deviceSize: string;
    isBrowser: boolean;
    isServer: boolean;
} & T;

type BuilderProps<T> = {
    builderState: {
        state: BuilderState<T>;
    };
};

type S38NearbyLocationsSectionProps = BuilderProps<{
    metro: Metro;
    metroThumbnails: Record<string, string>;
    state: State;
    store: Store;
    staff: MedicalDirector[];
    reviewsData?: ReviewsData;
    nearbyLocationReviews?: Record<string, ReviewsData | null>;
}> & {
    phoneIcon: string;
    mapIcon: string;
    desktopPaddingTop?: PaddingSize;
    desktopPaddingBottom?: PaddingSize;
    mobilePaddingTop?: PaddingSize;
    mobilePaddingBottom?: PaddingSize;
    backgroundColor?: MilanBackgroundColors;
};

export function S38NearbyLocationsSection(props: S38NearbyLocationsSectionProps) {
    const {
        desktopPaddingTop = 'large',
        desktopPaddingBottom = 'large',
        mobilePaddingTop = 'small',
        mobilePaddingBottom = 'small',
        backgroundColor,
        phoneIcon,
        mapIcon,
        ...restProps
    } = props;

    const {
        builderState: { state },
    } = restProps;

    const hasState = state && state?.store && state?.metro && state?.metroThumbnails;
    if (!hasState) {
        return <h2 className="py-4">Oops! This component can only be used on clinic pages.</h2>;
    }

    const { metroThumbnails, metro, store } = state;
    const closestStores = getClosestStores(store.geo.lat, store.geo.long, metro.stores, store.marketingCity);

    const nearbyLocations = closestStores
        .filter((clinic) => {
            const trimmed = hardTrim(clinic.marketingCity).toLowerCase();
            return metroThumbnails[trimmed] !== undefined;
        })
        .map((clinic) => {
            const { phone, address, marketingCity, addressCity, zipCode, salesforceValue } = clinic;
            const trimmed = hardTrim(marketingCity).toLowerCase();

            const clinicReviews = state?.nearbyLocationReviews?.[salesforceValue];
            const rating = clinicReviews
                ? {
                      value: clinicReviews.average ? parseFloat(clinicReviews.average) : 0,
                      reviewCount: clinicReviews.total,
                  }
                : undefined;

            return {
                name: marketingCity,
                href: clinic.urlPath,
                image: metroThumbnails[trimmed],
                imageAltText: marketingCity,
                address: {
                    line1: address,
                    line2: `${addressCity || marketingCity}, ${state.state.abbreviation} ${zipCode}`,
                },
                phone,
                rating,
            };
        });

    if (nearbyLocations.length === 0) {
        return null; // If there are no nearby locations, this module should not show
    }

    const topPaddingClasses = `mcl-pt-${paddingMap[mobilePaddingTop]} lg:mcl-pt-${paddingMap[desktopPaddingTop]}`;
    const bottomPaddingClasses = `mcl-pb-${paddingMap[mobilePaddingBottom]} lg:mcl-pb-${paddingMap[desktopPaddingBottom]}`;
    const { backgroundStyles, url } = useBackgroundOptions({
        backgroundWave: true,
        backgroundWaveColor: 'White',
        flipWave: false,
        waveGradient: 'default',
        backgroundGradient: 'default',
        backgroundColor: processBackgroundColor({
            backgroundColor,
        }),
    });

    const locationsWithIcons = nearbyLocations.map((loc) => ({
        mapPinImage: mapIcon,
        mapPinImageAlt: 'Location icon',
        phoneImage: phoneIcon,
        phoneImageAlt: 'Phone icon',
        ...loc,
    }));

    return (
        <div
            style={{ backgroundImage: url }}
            className={`mcl-bg-contain mcl-bg-top mcl-bg-no-repeat ${backgroundStyles} ${topPaddingClasses} ${bottomPaddingClasses}`}
        >
            <div className="container mcl-flex mcl-flex-col mcl-items-center gap-12">
                <Typography color="Blue" tag="h2" variant="h2-large">
                    Nearby Locations
                </Typography>
                <S38NearbyLocations locations={locationsWithIcons} />
            </div>
        </div>
    );
}
